import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { tipsApi } from "@/services/api/tips";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { TipSortControls, type SortOption } from "@/components/tips/TipSortControls";
import { TipsGrid } from "@/components/tips/TipsGrid";
import { TipSubmission } from "@/components/TipSubmission";
import { motion } from "framer-motion";
import Intro from "@/components/ContentsIntro";
import { gsap } from "gsap";
import { getActiveUniversity } from "@/utils/prize/universityUtils";

document.addEventListener('gesturestart', function (e) {
  e.preventDefault();
});

document.addEventListener('wheel', function (e) {
  if (e.ctrlKey) {
      e.preventDefault();
  }
}, { passive: false });

document.addEventListener('keydown', function (e) {
  if (e.ctrlKey && (e.key === '=' || e.key === '-' || e.key === '0')) {
      e.preventDefault();
  }
});

const Index = () => {
  const { data: tips = [], isLoading, error } = useQuery({
    queryKey: ["approved-tips"],
    queryFn: tipsApi.fetchApprovedTips,
    retry: 2,
  });

  const [sortBy, setSortBy] = useState<SortOption>("random"); // Set default to "random"
  const [shuffleCounter, setShuffleCounter] = useState(0); // Counter to trigger reshuffle
  const [isIntroComplete, setIsIntroComplete] = useState(false);

  const sortedTips = useMemo(() => {
    console.log('Sorting tips by:', sortBy);
    console.log('Current tips:', tips);
    
    if (!tips || tips.length === 0) {
      console.log('No tips to sort');
      return [];
    }

    const tipsCopy = [...tips]; // Create a copy for sorting
    
    switch (sortBy) {
      case "popular": {
        return tipsCopy.sort((a, b) => {
          const totalVotesA = (a.unsw_votes || 0) + (a.usyd_votes || 0) + (a.uoa_votes || 0);
          const totalVotesB = (b.unsw_votes || 0) + (b.usyd_votes || 0) + (b.uoa_votes || 0);
          return totalVotesB - totalVotesA;
        });
      }
      case "latest":
        return tipsCopy.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      case "random":
        // Shuffle tips using a new shuffle counter
        return tipsCopy.sort(() => Math.random() - 0.5); // Randomize order
      default:
        return tipsCopy;
    }
  }, [tips, sortBy, shuffleCounter]); // Include shuffleCounter in dependencies

  const handleSortChange = (newSortOption: SortOption) => {
    console.log('Changing sort option to:', newSortOption);
    
    if (newSortOption === "random") {
      setShuffleCounter(prev => prev + 1); // Increment counter to trigger reshuffle
    }
    
    setSortBy(newSortOption); // Update sort option
  };

  useEffect(() => {
    let tl_repeat: gsap.core.Timeline | null = null;
    let tl_yoyo: gsap.core.Timeline | null = null;
    let tl_logo: gsap.core.Timeline | null = null;
    let tl_lockup: gsap.core.Timeline | null = null;

    if (!isIntroComplete) {
      tl_repeat = gsap.timeline({ repeat: -1, yoyo: true});
      tl_repeat.to(".KV-1_G", { scale: 1.3, duration: 1, ease: "power1.inOut" });

      tl_yoyo = gsap.timeline({ repeat: -1, yoyo: true});
      tl_yoyo.fromTo("#KV-5_G", { scale: 1.0 }, { y:"-30px", scale: 1.1, duration: 1.5, ease: "power1.inOut" });

      tl_logo = gsap.timeline();
      tl_logo
        .to("#mainComp, #KV, #mainCopy_G, #mainOptions_G", { opacity: 1, duration: 0.1 })
        .from("#mainLogo_G", { scale: "0", duration: 1.5, ease: "power1.inOut" })
        .from("#mainLogo", { x: "159px", duration: 1.5, ease: "power1.inOut" }, "-=1")
        .from("#logo2", { x: "-100%", duration: 1.5, ease: "power1.inOut" }, "<");

      tl_lockup = gsap.timeline();
      tl_lockup
        .from("#KV-1", { opacity: 0, scale: 0, duration: 1.5, ease: "power1.inOut" })
        .from("#KV-1B", { opacity: 0, scale: 0, duration: 1.5, ease: "power1.inOut" }, "-=1.3")
        .from("#KV-4", { scale: 0, duration: 1.5, ease: "elastic.out(1, 0.5)" }, "-=1.0")
        .from("#KV-3", { scale: 0, duration: 1.5, ease: "elastic.out(1, 0.5)" }, "-=1.3")
        .from("#KV-5, #KV-2", { scale: 0, duration: 1.5, ease: "elastic.out(1, 0.5)" }, "-=1.4")
        .from("#KV-6", { scale: 0, duration: 1.5, ease: "elastic.out(1, 0.5)" }, "-=1.4")
        .from("#KV-7", { scale: 0, duration: 1.5, ease: "elastic.out(1, 0.5)" }, "-=1.4")
        .from("#copy1, #copy1_cover", { scale: 0, duration: 1.5, ease: "elastic.out(1, 0.7)" }, "-=1.3")
        .from("#copy2, #copy2_cover", { scale: 0, duration: 1.5, ease: "elastic.out(1, 0.7)" }, "-=1.4")
        .from(".shine", { opacity: 0, duration: 0.1 }, "-=0.5")
        .from(".intro_copy", { y: 30, opacity: 0, duration: 1.5, ease: "elastic.out(1, 0.7)" }, "-=1.4")
        .from("#opt1", { scale: 0, duration: 1.5, ease: "elastic.out(1, 0.5)" }, "-=1.4")
        .from("#opt1_C", { y: 10, opacity: 0, duration: 1.5, ease: "elastic.out(1, 0.7)" }, "-=1.4")
        .from("#opt2", { scale: 0, duration: 1.5, ease: "elastic.out(1, 0.5)" }, "-=1.5")
        .from("#opt2_C", { y: 10, opacity: 0, duration: 1.5, ease: "elastic.out(1, 0.7)" }, "-=1.4")
        .from("#opt3", { scale: 0, duration: 1.5, ease: "elastic.out(1, 0.5)" }, "-=1.5")
        .from("#opt3_C", { y: 10, opacity: 0, duration: 1.5, ease: "elastic.out(1, 0.7)" }, "-=1.4")
        .fromTo("#btn1_G", { scale: 0, opacity: 0 }, { scale: 1, opacity: 1, duration: 1.5, ease: "elastic.out(1, 0.7)" }, "-=1.4");
    }

    const btn1_G = document.getElementById("btn1_G");
    if (btn1_G) {
      const handleClick = () => {
        const tl_f1_out = gsap.timeline({ 
          repeat: 0,
          paused: true,
          onComplete: () => setIsIntroComplete(true)
        });
        
        tl_f1_out
          .to("#mainComp", { y: "-200vh", duration: 1.5, ease: "power1.inOut" })
          .to("#KV", { y: "-200vh", duration: 1.5, ease: "power1.inOut" }, "-=1.4")
          .to("#mainCopy_G", { y: "-200vh", duration: 1.5, ease: "power1.inOut" }, "-=1.4")
          .to(".intro_copy", { y: "-200vh", duration: 1.5, ease: "power1.inOut" }, "-=1.4")
          .to("#mainOptions_G", { y: "-200vh", duration: 1.5, ease: "power1.inOut" }, "-=1.4")
          .to("#btn1_G", { y: "-200vh", duration: 1.5, ease: "power1.inOut" }, "-=1.4")
          .to("#mainComp_G", { display: "none" }, "-=1.0")
          .fromTo("#tips-section", 
            { y: "50vh", opacity: 0, display: "none" }, 
            { y: 0, opacity: 1, display: "block", duration: 1 }, 
            "-=0.5"
          )
          .fromTo(".ele1", { scale:0 }, { scale: 1, duration: 2, ease: "elastic.out(1, 0.3)" }, "-=0.5")
          .fromTo(".ele4", { scale:0 }, { scale: 1, duration: 2, ease: "elastic.out(1, 0.3)" }, "-=1.8")
          .fromTo(".ele3", { scale:0 }, { scale: 1, duration: 2, ease: "elastic.out(1, 0.3)" }, "-=1.8")
          .fromTo(".ele5", { scale:0 }, { scale: 1, duration: 2, ease: "elastic.out(1, 0.3)" }, "-=1.8")
          .fromTo(".ele2", { scale:0 }, { scale: 1, duration: 2, ease: "elastic.out(1, 0.3)" }, "-=1.8")
          ;
        
        tl_f1_out.play();

        if (tl_repeat) tl_repeat.kill();
        if (tl_yoyo) tl_yoyo.kill();
        if (tl_logo) tl_logo.kill();
        if (tl_lockup) tl_lockup.kill();
      };

      btn1_G.addEventListener("click", handleClick);

      return () => {
        btn1_G.removeEventListener("click", handleClick);
        if (tl_repeat) tl_repeat.kill();
        if (tl_yoyo) tl_yoyo.kill();
        if (tl_logo) tl_logo.kill();
        if (tl_lockup) tl_lockup.kill();
      };
    }
  }, [isIntroComplete]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br">
        <Alert variant="destructive" className="max-w-lg backdrop-blur-sm bg-white/10">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Error loading tips. Please try again later.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gradient-to-br relative overflow-hidden"
    >
      <Intro />

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.6 }}
        id="tips-section"
        className="container mx-auto px-4 pb-16 pt-0"
        style={{ display: isIntroComplete ? 'block' : 'none' }}
      >
        <div className="flex justify-center">
          <div className="w-[100px]">
            <div className="ele1"><img src="/imgs/heart2.png" alt="Live Your Best Lip!" className="w-[200px] h-auto floating4 mt-[250px]" id="ele1" /></div>
            <div className="ele2"><img src="/imgs/asterisk.png" alt="Live Your Best Lip!" className="w-[200px] h-auto floating3 mt-[800px] ml-[20px]" id="ele2" /></div>
            <div className="ele3"><img src="/imgs/doddle.png" alt="Live Your Best Lip!" className="w-[80px] h-auto floating4 mt-[300px] ml-[100px]" id="ele3" /></div>
          </div>

          <div className="flex-1 max-w-[70vw] mx-auto">
            <TipSortControls 
              sortBy={sortBy} 
              onSortChange={handleSortChange} 
            />
            <TipsGrid 
              tips={sortedTips} 
              isLoading={isLoading} 
              key={`tips-grid-${sortBy}-${shuffleCounter}`} // Forces re-render
            />
          </div>

          <div className="w-[100px]">
            <div className="ele4"><img src="/imgs/ball.png" alt="Live Your Best Lip!" className="w-[200px] h-auto floating3 mt-[500px] ml-[-20px]" id="ele4" /></div>
            <div className="ele5"><img src="/imgs/heart2.png" alt="Live Your Best Lip!" className="w-[200px] h-auto floating4 mt-[1000px] ml-[10px]" id="ele5" /></div>
          </div>
        </div>
      </motion.div>

      <div className="hidden">
        <TipSubmission />
      </div>
    </motion.div>
  );
};

export default Index;